import axios from 'axios';

// Smart environment detection - must be called at runtime, not build time
const isDev = () => typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

export const getServerURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (typeof window !== 'undefined' && !isDev()) return 'https://watchdealer-crm-backend.onrender.com';
  return 'http://localhost:8000';
};

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token AND dynamic base URL
api.interceptors.request.use(
  (config) => {
    // Set baseURL dynamically at request time
    config.baseURL = `${getServerURL()}/api`;

    // Add auth token (stored in localStorage; ensure it's a non-empty string)
    const token = localStorage.getItem('token');
    if (token && typeof token === 'string' && token.trim().length > 0) {
      config.headers.Authorization = `Bearer ${token.trim()}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle both 401 (Unauthorized) and 403 (Forbidden) as auth failures
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    // 403: do not clear token (e.g. pending approval) — let the calling UI show the message
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getCurrentUser: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  verifyEmail: (token) => api.post('/auth/verify-email', { token }),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  // Security: data export, delete account, MFA
  exportMyData: () => api.get('/auth/me/export'),
  deleteAccount: (password) => api.post('/auth/me/delete-account', { password }),
  mfaStatus: () => api.get('/auth/mfa/status'),
  mfaSetup: () => api.post('/auth/mfa/setup'),
  mfaVerify: (code) => api.post('/auth/mfa/verify', { code }),
  mfaDisable: (password, code) => api.post('/auth/mfa/disable', { password, code }),
};

export const watchAPI = {
  getAll: () => api.get('/watches/'),
  getOne: (id) => api.get(`/watches/${id}`),
  create: (data) => api.post('/watches/', data),
  update: (id, data) => api.put(`/watches/${id}`, data),
  delete: (id) => api.delete(`/watches/${id}`),
  getStats: () => api.get('/watches/stats/summary'),
};

export const clientAPI = {
  getAll: (params) => api.get('/clients/', { params }),
  getOne: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients/', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  getStats: () => api.get('/clients/stats/summary'),
};

export const salesAPI = {
  getAll: (params) => api.get('/sales/', { params }),
  getOne: (id) => api.get(`/sales/${id}`),
  getTypes: () => api.get('/sales/types'),
  create: (data) => api.post('/sales/', data),
  getStats: () => api.get('/sales/stats/summary'),
};

export const offerAPI = {
  getAll: () => api.get('/offers/'),
  getOne: (id) => api.get(`/offers/${id}`),
  create: (data) => api.post('/offers/', data),
  update: (id, data) => api.put(`/offers/${id}`, data),
  delete: (id) => api.delete(`/offers/${id}`),
};

export const imageAPI = {
  getAll: (watchId) => api.get(`/images/watches/${watchId}`),
  upload: (watchId, files) => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });
    return api.post(`/images/watches/${watchId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (watchId, imageUrl) => {
    // Backend handles full Cloudinary URLs directly - encode as base64 to preserve URL structure
    const encodedUrl = btoa(imageUrl);
    return api.delete(`/images/watches/${watchId}/${encodedUrl}`);
  },
};

export const marketplaceAPI = {
  getAll: (params) => api.get('/marketplace/', { params }),
  getOne: (id) => api.get(`/marketplace/${id}`),
};

export const serviceAPI = {
  getAll: (params) => api.get('/services/', { params }),
  getOne: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services/', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  getStats: () => api.get('/services/stats/summary'),
};

export const connectionAPI = {
  getDirectory: (params) => api.get('/connections/directory', { params }),
  getProfile: (dealerId) => api.get(`/connections/profile/${dealerId}`),
  sendRequest: (dealerId) => api.post(`/connections/request/${dealerId}`),
  acceptRequest: (connectionId) => api.post(`/connections/accept/${connectionId}`),
  declineRequest: (connectionId) => api.post(`/connections/decline/${connectionId}`),
  removeConnection: (connectionId) => api.delete(`/connections/${connectionId}`),
  getMyConnections: () => api.get('/connections/my-connections'),
  getPendingRequests: () => api.get('/connections/pending-requests'),
};

export const messagesAPI = {
  sendMessage: (data) => api.post('/messages/', data),
  getInbox: () => api.get('/messages/inbox'),
  getSent: () => api.get('/messages/sent'),
  getConversations: () => api.get('/messages/conversations'),
  getThread: (userId) => api.get(`/messages/thread/${userId}`),
  markAsRead: (messageId) => api.put(`/messages/${messageId}/read`),
  markThreadAsRead: (userId) => api.put(`/messages/thread/${userId}/read-all`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  deleteThread: (userId) => api.delete(`/messages/thread/${userId}`),
};

export const wishListAPI = {
  getAll: (params) => api.get('/wishlists/', { params }),
  getOne: (id) => api.get(`/wishlists/${id}`),
  create: (data) => api.post('/wishlists/', data),
  update: (id, data) => api.put(`/wishlists/${id}`, data),
  delete: (id) => api.delete(`/wishlists/${id}`),
  markFulfilled: (id, watchId) => api.post(`/wishlists/${id}/fulfill/${watchId}`),
  getMatches: (id) => api.get(`/wishlists/${id}/matches`),
  getPublic: (params) => api.get('/wishlists/network/public', { params }),
};

export const analyticsAPI = {
  getFinancialSummary: (period = 'month') => api.get('/analytics/financial-summary', { params: { period } }),
  getInventoryTurnover: () => api.get('/analytics/inventory-turnover'),
};

export const appointmentsAPI = {
  getAll: (params) => api.get('/appointments/', { params }),
  getOne: (id) => api.get(`/appointments/${id}`),
  create: (data) => api.post('/appointments/', data),
  update: (id, data) => api.put(`/appointments/${id}`, data),
  delete: (id) => api.delete(`/appointments/${id}`),
  getUpcoming: (limit = 5) => api.get('/appointments/upcoming', { params: { limit } }),
  getCalendar: (month, year) => api.get('/appointments/calendar', { params: { month, year } }),
  getStats: () => api.get('/appointments/stats/summary'),
};

export const invoiceTemplateAPI = {
  get: () => api.get('/invoice-template/'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/invoice-template/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: () => api.delete('/invoice-template/'),
};

export const invoicesAPI = {
  getAll: (params) => api.get('/invoices/', { params }),
  getOne: (id) => api.get(`/invoices/${id}`),
  getPreviewData: (id) => api.get(`/invoices/${id}/preview-data`),
  create: (data) => api.post('/invoices/', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  generatePDF: (id) => api.post(`/invoices/${id}/generate-pdf`),
  /** Fetch PDF as blob (use response.data then URL.createObjectURL to open in new tab and avoid "Failed to load PDF"). */
  getPDF: (id, versionId = null) =>
    api.get(`/invoices/${id}/pdf`, { params: versionId != null ? { version_id: versionId } : {}, responseType: 'blob' }),
  generateFillablePDF: (id) => api.post(`/invoices/${id}/generate-fillable-pdf`),
  uploadPDF: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/invoices/${id}/upload-pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getVersions: (id) => api.get(`/invoices/${id}/versions`),
  sendEmail: (id) => api.post(`/invoices/${id}/send-email`),
  getStats: () => api.get('/invoices/stats/summary'),
};

export const consignmentsAPI = {
  getAll: (params) => api.get('/consignments/', { params }),
  getOne: (id) => api.get(`/consignments/${id}`),
  create: (data) => api.post('/consignments/', data),
  update: (id, data) => api.put(`/consignments/${id}`, data),
  delete: (id) => api.delete(`/consignments/${id}`),
  getStats: () => api.get('/consignments/stats/summary'),
};

export const watchLookupAPI = {
  lookup: (reference) => api.get(`/watch-lookup/${reference}`),
  searchChrono24: (query) => api.get('/watch-lookup/search', { params: { query } }),
};

export const chronoAPI = {
  research: (data) => api.post('/chrono/research', data),
  getHistory: () => api.get('/chrono/history'),
};

export const adminAPI = {
  getAllUsers: () => api.get('/admin/users'),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  getSystemStats: () => api.get('/admin/stats'),
  getPendingUsers: () => api.get('/admin/users/pending'),
  approveUser: (id) => api.post(`/admin/users/${id}/approve`),
  rejectUser: (id) => api.post(`/admin/users/${id}/reject`),
};

export const publicAPI = {
  getDealerProfile: (dealerId) => api.get(`/public/dealers/${dealerId}`),
  getDealerListings: (dealerId, params) => api.get(`/public/dealers/${dealerId}/listings`, { params }),
};

export const migrationsAPI = {
  runMigration: (name) => api.post(`/migrations/${name}`),
  getStatus: () => api.get('/migrations/status'),
};

export const accountingAPI = {
  // Dealer dashboard (single view: cash, receivables, payables, compliance, inventory, recent activity)
  getDashboard: () => api.get('/accounting/dashboard'),

  // Helpers for forms
  getAccountTypes: () => api.get('/accounting/account-types'),
  getExpenseCategories: () => api.get('/accounting/expense-categories'),
  getPaymentTerms: () => api.get('/accounting/payment-terms'),
  getDepreciableAssetCategories: () => api.get('/accounting/depreciable-assets/categories'),

  // Chart of Accounts
  getAccounts: () => api.get('/accounting/accounts'),
  getAccount: (id) => api.get(`/accounting/accounts/${id}`),
  createAccount: (data) => api.post('/accounting/accounts/', data),
  updateAccount: (id, data) => api.put(`/accounting/accounts/${id}`, data),
  deleteAccount: (id) => api.delete(`/accounting/accounts/${id}`),
  seedDefaultAccounts: () => api.post('/accounting/accounts/seed-defaults'),

  // Journal Entries
  getJournalEntries: (params) => api.get('/journal-entries/', { params }),
  getJournalEntry: (id) => api.get(`/journal-entries/${id}`),
  getJournalEntryStats: () => api.get('/journal-entries/stats/summary'),
  getJournalEntryTypes: () => api.get('/journal-entries/types/list'),
  createJournalEntry: (data) => api.post('/journal-entries/', data),
  postJournalEntry: (id) => api.post(`/journal-entries/${id}/post`),

  // Vendors
  getVendors: () => api.get('/accounting/vendors'),
  getVendor: (id) => api.get(`/accounting/vendors/${id}`),
  createVendor: (data) => api.post('/accounting/vendors/', data),
  updateVendor: (id, data) => api.put(`/accounting/vendors/${id}`, data),
  deleteVendor: (id) => api.delete(`/accounting/vendors/${id}`),

  // Expenses
  getExpenses: (params) => api.get('/accounting/expenses', { params }),
  getExpense: (id) => api.get(`/accounting/expenses/${id}`),
  createExpense: (data) => api.post('/accounting/expenses/', data),
  updateExpense: (id, data) => api.put(`/accounting/expenses/${id}`, data),
  deleteExpense: (id) => api.delete(`/accounting/expenses/${id}`),

  // Resale Certificates
  getResaleCertificates: (params) => api.get('/accounting/resale-certificates', { params }),
  getResaleCertificate: (id) => api.get(`/accounting/resale-certificates/${id}`),
  createResaleCertificate: (data) => api.post('/accounting/resale-certificates/', data),
  updateResaleCertificate: (id, data) => api.put(`/accounting/resale-certificates/${id}`, data),
  deleteResaleCertificate: (id) => api.delete(`/accounting/resale-certificates/${id}`),
  verifyResaleCertificate: (id) => api.post(`/accounting/resale-certificates/${id}/verify`),

  // Depreciable Assets
  getDepreciableAssets: (params) => api.get('/accounting/depreciable-assets', { params }),
  getDepreciableAsset: (id) => api.get(`/accounting/depreciable-assets/${id}`),
  createDepreciableAsset: (data) => api.post('/accounting/depreciable-assets/', data),
  updateDepreciableAsset: (id, data) => api.put(`/accounting/depreciable-assets/${id}`, data),
  deleteDepreciableAsset: (id) => api.delete(`/accounting/depreciable-assets/${id}`),

  // Recurring Transactions
  getRecurringTransactions: () => api.get('/accounting/recurring-transactions'),
  getRecurringTransaction: (id) => api.get(`/accounting/recurring-transactions/${id}`),
  createRecurringTransaction: (data) => api.post('/accounting/recurring-transactions/', data),
  updateRecurringTransaction: (id, data) => api.put(`/accounting/recurring-transactions/${id}`, data),
  deleteRecurringTransaction: (id) => api.delete(`/accounting/recurring-transactions/${id}`),
  toggleRecurringTransaction: (id) => api.post(`/accounting/recurring-transactions/${id}/toggle`),

  // Payment Plans
  getPaymentPlans: (params) => api.get('/accounting/payment-plans', { params }),
  getPaymentPlan: (id) => api.get(`/accounting/payment-plans/${id}`),
  createPaymentPlan: (data) => api.post('/accounting/payment-plans/', data),
  updatePaymentPlan: (id, data) => api.put(`/accounting/payment-plans/${id}`, data),
  deletePaymentPlan: (id) => api.delete(`/accounting/payment-plans/${id}`),
  recordPayment: (id, data) => api.post(`/accounting/payment-plans/${id}/record-payment`, data),

  // Reports
  getBalanceSheet: (params) => api.get('/accounting/reports/balance-sheet', { params }),
  getProfitAndLoss: (params) => api.get('/accounting/reports/profit-loss', { params }),
  getCashFlow: (params) => api.get('/accounting/reports/cash-flow', { params }),
  getTrialBalance: (params) => api.get('/accounting/reports/trial-balance', { params }),
  getGeneralLedger: (params) => api.get('/accounting/reports/general-ledger', { params }),

  // Report Exports
  exportBalanceSheetPDF: (params) => api.get('/accounting/reports/balance-sheet/pdf', { params, responseType: 'blob' }),
  exportProfitAndLossPDF: (params) => api.get('/accounting/reports/profit-loss/pdf', { params, responseType: 'blob' }),
  exportTrialBalancePDF: (params) => api.get('/accounting/reports/trial-balance/pdf', { params, responseType: 'blob' }),
  exportCashFlowPDF: (params) => api.get('/accounting/reports/cash-flow/pdf', { params, responseType: 'blob' }),
  exportARAgingPDF: (params) => api.get('/accounting/receivables/aging-report/pdf', { params, responseType: 'blob' }),
  exportAPAgingPDF: (params) => api.get('/accounting/payables/aging-report/pdf', { params, responseType: 'blob' }),

  // Additional Reports
  getARAgingReport: (params) => api.get('/accounting/receivables/aging-report', { params }),
  getAPAgingReport: (params) => api.get('/accounting/payables/aging-report', { params }),
  getDashboardMetrics: (params) => api.get('/accounting/metrics/dashboard', { params }),
  getInventoryValuation: (params) => api.get('/accounting/inventory/valuation', { params }),
  getInventoryDetail: (params) => api.get('/accounting/inventory/detail', { params }),

  // Accounting settings (QuickBooks-style closing date)
  getClosingDate: () => api.get('/accounting/settings/closing-date'),
  setClosingDate: (body) => api.put('/accounting/settings/closing-date', body),
};

export default api;
